"use client";

import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import Link from "next/link";
import Image from "next/image";

export default function MainHero({
  title,
  img = "/about-us.png",
}: {
  title: string;
  img?: string;
}) {
  const t = useTranslations("Nav");

  return (
    <section className="relative w-full min-h-[400px] flex items-center overflow-hidden bg-brand-navy">
      {/* 🖼️ Background Image Layer */}
      <motion.div
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.4 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
        className="absolute inset-0 z-0"
      >
        <Image
          src={img}
          alt="Hero background"
          fill
          priority
          className="object-cover object-center grayscale-30"
        />
      </motion.div>

      {/* 🌑 Overlay Gradient - Adjusted for readability */}
      <div className="absolute inset-0 z-10 bg-linear-to-r from-brand-navy via-brand-navy/80 to-transparent md:to-brand-navy/20" />
      <div className="absolute inset-0 z-10 bg-linear-to-t from-brand-navy via-transparent to-transparent md:hidden" />

      {/* 🧭 Vertical Line Decoration - Hidden on small screens */}
      <div className="absolute left-6 md:left-12 lg:left-20 top-0 bottom-0 w-px bg-brand-creamy/10 z-20 hidden sm:block" />

      {/* 🎯 Main Content Container */}
      <div className="container mx-auto px-6 sm:px-12 md:px-24 lg:px-32 relative z-30">
        <div className="max-w-full md:max-w-5xl">
          {/* 🔗 Responsive Breadcrumb */}
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex items-center gap-3 md:gap-4 mb-4 md:mb-8 text-brand-creamy/60 text-[10px] md:text-xs tracking-[0.2em] md:tracking-[0.4em] uppercase font-bold"
          >
            <Link
              href="/"
              className="hover:text-brand-blue transition-colors whitespace-nowrap"
            >
              {t("home")}
            </Link>
            <span className="w-4 md:w-10 h-px bg-brand-red shrink-0" />
            <span className="text-brand-creamy truncate">{t(title)}</span>
          </motion.nav>

          {/* 🏆 Title with Responsive Text Sizes */}
          <div className="relative overflow-hidden py-2">
            <motion.h1
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="text-[12vw] sm:text-[10vw] md:text-[8vw] lg:text-[7vw] xl:text-[80px] font-black text-white uppercase leading-[0.9] tracking-tighter"
            >
              {t(title)}
            </motion.h1>
          </div>
        </div>
      </div>

      {/* 🎨 Mobile-only Decorative Element */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-linear-to-r from-brand-red via-brand-blue to-transparent md:hidden" />
    </section>
  );
}
